import { Alert, Button, CircularProgress, Grid, gridClasses, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getManifestDownloadUrl,
  getUploadList,
  setStudyUploadVisibility,
  StudyUploadVisibilityOperation,
} from 'api/uploads';
import LabelledDropdown from 'components/atoms/Dropdown/LabelledDropdown';
import { CountedToggleButtonGroup } from 'components/atoms/CountedToggleButtonGroup';
import wrapPage from 'components/atoms/wrapPage/wrapPage';
import JobModal from 'components/JobModal';
import Loader, { LoadingStage } from 'components/Loader';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import PageHeader from 'components/PageHeader';
import { NON_SPECIFIC_STUDY_VALUES } from 'components/SearchFilters/hooks/useMainFilters';
import { getPendingSlidesLocation } from 'components/StudyDashboard/ProceduresPage/pendingSlidesNavigation';
import { canQueryPendingSlides } from 'components/StudyDashboard/ProceduresPage/pendingSlidesPermissions';
import { useErrorSnackbarWithRetry } from 'components/Snackbars/useErrorSnackbarWithRetry';
import {
  buildUploadCategoryRows,
  getUploadDashboardLifecycle,
  getUploadRowsForFilter,
  getManifestDialogUrlForUploads,
  isUploadIngestActionAvailable,
  UploadFilter,
  uploadFilterLabels,
  uploadFilters,
} from 'components/UploadsDashboard/helpers';
import { concat, filter, find, includes, map, orderBy, size, some, toString } from 'es-toolkit/compat';
import { FileInfo, UploadDashboardRow, UploadListResponse } from 'interfaces/fileList';
import { JobType } from 'interfaces/job';
import { Permission } from 'interfaces/permissionOption';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { ResponseError } from 'superagent';
import { useStudies } from 'utils/queryHooks/useStudies';
import { invalidateSlideVisibility } from 'utils/queryHooks/invalidateSlideVisibility';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { usePermissions } from 'utils/usePermissions';
import BulkUploadIngestExecutionDialog from './BulkUploadIngestExecutionDialog';
import UploadIngestExecutionDialog from './UploadIngestExecutionDialog';
import UploadsTable from './UploadsTable';
import { TestLabSourceIngest } from './TestLabSourceIngest';

const defaultUploadFilters = filter(uploadFilters, (uploadFilter) => uploadFilter !== 'hidden');

const updateUploadVisibility = (
  currentUploads: UploadListResponse | undefined,
  row: UploadDashboardRow,
  operation: StudyUploadVisibilityOperation
): UploadListResponse | undefined => {
  if (!currentUploads) {
    return currentUploads;
  }

  const isHidden = operation === 'hide';
  const optimisticUpload = {
    ...(find(currentUploads.all, (upload) => upload.objectKey === row.objectKey) || row),
    isHidden,
  };
  const withoutOptimisticUpload = (uploads: FileInfo[]) =>
    filter(uploads, (upload) => upload.objectKey !== row.objectKey);
  const pendingUploads = withoutOptimisticUpload(currentUploads.slides.pending);
  const restoredLifecycle = getUploadDashboardLifecycle({
    ...optimisticUpload,
    isHidden: false,
    uploadCategory: row.uploadCategory,
  });
  const shouldRestoreToPending =
    operation === 'restore' &&
    Boolean(optimisticUpload.uploadKey) &&
    restoredLifecycle.actionCategory !== 'organizeSlides' &&
    restoredLifecycle.actionCategory !== 'openAssignedCase';
  const shouldRestoreToProcessed =
    operation === 'restore' && Boolean(optimisticUpload.uploadKey) && !shouldRestoreToPending;

  return {
    ...currentUploads,
    slides: {
      ...currentUploads.slides,
      pending: shouldRestoreToPending ? concat(pendingUploads, [optimisticUpload]) : pendingUploads,
      processed: shouldRestoreToProcessed
        ? concat(withoutOptimisticUpload(currentUploads.slides.processed), [optimisticUpload])
        : withoutOptimisticUpload(currentUploads.slides.processed),
      failed: withoutOptimisticUpload(currentUploads.slides.failed),
    },
    all: map(currentUploads.all, (upload) => (upload.objectKey === row.objectKey ? { ...upload, isHidden } : upload)),
  };
};

const restoreUploadInCollection = (current: FileInfo[], previous: FileInfo[], objectKey: string) => {
  const withoutTarget = filter(current, (upload) => upload.objectKey !== objectKey);
  const previousIndex = previous.findIndex((upload) => upload.objectKey === objectKey);
  if (previousIndex < 0) {
    return withoutTarget;
  }

  const restored = [...withoutTarget];
  restored.splice(Math.min(previousIndex, restored.length), 0, previous[previousIndex]);
  return restored;
};

const restoreUploadVisibility = (
  currentUploads: UploadListResponse | undefined,
  previousUploads: UploadListResponse | undefined,
  objectKey: string
) => {
  if (!currentUploads || !previousUploads) {
    return currentUploads;
  }

  return {
    ...currentUploads,
    slides: {
      ...currentUploads.slides,
      pending: restoreUploadInCollection(currentUploads.slides.pending, previousUploads.slides.pending, objectKey),
      processed: restoreUploadInCollection(
        currentUploads.slides.processed,
        previousUploads.slides.processed,
        objectKey
      ),
      failed: restoreUploadInCollection(currentUploads.slides.failed, previousUploads.slides.failed, objectKey),
    },
    all: restoreUploadInCollection(currentUploads.all, previousUploads.all, objectKey),
  };
};

export const UploadsDashboard: React.FunctionComponent<React.PropsWithChildren<unknown>> = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirmation();
  const { enqueueErrorSnackbar } = useErrorSnackbarWithRetry();
  const { labId, currentLab } = useCurrentLabId();
  const { hasPermission } = usePermissions();
  const { encodedFilters, queryParams, setQueryParams } = useEncodedFilters();
  const { data: studies, isLoading: studiesLoading } = useStudies(labId);
  const sortedStudies = orderBy(studies || [], ['name'], ['asc']);
  const studyOptions = map(sortedStudies, (study) => ({ value: study.id, text: study.name }));
  const currentStudy = find(studies, { id: queryParams.filters?.studyId });
  const [activeFilters, setActiveFilters] = React.useState<UploadFilter[]>(() => {
    const validRestoredFilters = filter(queryParams.uploadFilters || [], (value): value is UploadFilter =>
      includes(uploadFilters, value as UploadFilter)
    );
    return size(validRestoredFilters) ? validRestoredFilters : defaultUploadFilters;
  });
  const [downloadingManifestKey, setDownloadingManifestKey] = React.useState<string | null>(null);
  const pendingUploadVisibilityOperationsRef = React.useRef<Record<string, StudyUploadVisibilityOperation>>({});
  const [pendingUploadVisibilityOperations, setPendingUploadVisibilityOperations] = React.useState<
    Record<string, StudyUploadVisibilityOperation>
  >({});
  const [selectedUploadForExecution, setSelectedUploadForExecution] = React.useState<UploadDashboardRow | null>(null);
  const [selectedUploadForManualPostIngest, setSelectedUploadForManualPostIngest] =
    React.useState<UploadDashboardRow | null>(null);
  const [isBulkUploadIngestDialogOpen, setIsBulkUploadIngestDialogOpen] = React.useState(false);
  const selectedStudyId =
    queryParams.filters?.studyId && !includes(NON_SPECIFIC_STUDY_VALUES, toString(queryParams.filters?.studyId))
      ? toString(queryParams.filters?.studyId)
      : '';
  const canExecuteUploadIngest = hasPermission(Permission.ExecuteUploadedSlideIngest);
  const canReingestUploadedSlides =
    hasPermission(Permission.ReingestUploadedSlides) || Boolean(currentLab?.isTestLab && canExecuteUploadIngest);
  const canRunManualPostIngestPipeline = hasPermission(Permission.ExecuteManualPostIngestPipeline);
  const canUseSlidesManifest = hasPermission(Permission.ApplyCaseManifest);
  const canViewStudyPendingSlides = canQueryPendingSlides({ hasPermission, studyId: selectedStudyId });
  const canManageUploadVisibility = hasPermission(Permission.HideRestoreStudyUploads);

  const canLoadUploads =
    Boolean(queryParams.filters?.studyId) &&
    !includes(NON_SPECIFIC_STUDY_VALUES, toString(queryParams.filters?.studyId));
  const studyUploadsQueryKey = ['studyUploads', selectedStudyId, labId];
  const { data, isLoading, isFetching, isError, refetch } = useQuery<UploadListResponse>(
    studyUploadsQueryKey,
    () => getUploadList({ studyId: selectedStudyId, labId }),
    {
      refetchInterval: 120000,
      enabled: canLoadUploads,
    }
  );
  const isRefreshingUploads = isFetching && !isLoading;
  const pendingUploadRows: FileInfo[] = data?.slides?.pending ?? [];
  const completedUploadRows: FileInfo[] = data?.slides?.processed ?? [];
  const allUploadRows: FileInfo[] = data?.all ?? [];

  const storedSlideFileRows = concat(
    buildUploadCategoryRows(
      filter(pendingUploadRows, (row) => !row.isHidden),
      'awaitingIngest'
    ),
    buildUploadCategoryRows(
      filter(completedUploadRows, (row) => !row.isHidden),
      'awaitingIngest'
    )
  );
  const awaitingIngestRows = getUploadRowsForFilter(storedSlideFileRows, 'awaitingIngest');
  const assignedToCaseRows = getUploadRowsForFilter(storedSlideFileRows, 'assignedToCase');
  const missingCaseAssignmentRows = getUploadRowsForFilter(storedSlideFileRows, 'missingCaseAssignment');
  const slideUploadRows = concat(awaitingIngestRows, assignedToCaseRows, missingCaseAssignmentRows);
  const manifestRows = buildUploadCategoryRows(
    filter<FileInfo>(allUploadRows, (row) => Boolean(row.isManifest) && !row.isHidden),
    'manifest'
  );
  const otherRows = buildUploadCategoryRows(
    filter<FileInfo>(allUploadRows, (row) => !row.isHidden && !row.isManifest && !row.uploadKey),
    'other'
  );
  const hiddenRows = buildUploadCategoryRows(
    filter<FileInfo>(allUploadRows, (row) => row.isHidden),
    'hidden'
  );
  const uploadFilterCounts: Record<UploadFilter, number> = {
    awaitingIngest: size(awaitingIngestRows),
    assignedToCase: size(assignedToCaseRows),
    missingCaseAssignment: size(missingCaseAssignmentRows),
    manifest: size(manifestRows),
    other: size(otherRows),
    hidden: size(hiddenRows),
  };
  const enabledDefaultUploadFilters = filter(
    defaultUploadFilters,
    (uploadFilter) => uploadFilterCounts[uploadFilter] > 0
  );
  const visibleActiveFilters = size(filter(activeFilters, (uploadFilter) => uploadFilterCounts[uploadFilter] > 0))
    ? filter(activeFilters, (uploadFilter) => uploadFilterCounts[uploadFilter] > 0)
    : enabledDefaultUploadFilters;

  const rows = concat(
    includes(visibleActiveFilters, 'awaitingIngest') ? awaitingIngestRows : [],
    includes(visibleActiveFilters, 'assignedToCase') ? assignedToCaseRows : [],
    includes(visibleActiveFilters, 'missingCaseAssignment') ? missingCaseAssignmentRows : [],
    includes(visibleActiveFilters, 'manifest') ? manifestRows : [],
    includes(visibleActiveFilters, 'other') ? otherRows : [],
    includes(visibleActiveFilters, 'hidden') ? hiddenRows : []
  );
  const hasAnyUploads =
    size(
      concat(awaitingIngestRows, assignedToCaseRows, missingCaseAssignmentRows, manifestRows, otherRows, hiddenRows)
    ) > 0;
  const canExecuteBulkUploadIngest =
    canExecuteUploadIngest &&
    some(slideUploadRows, (row) =>
      isUploadIngestActionAvailable(row, getUploadDashboardLifecycle(row), canReingestUploadedSlides)
    );

  const handleFilterChange = (nextFilters: UploadFilter[]) => {
    if (size(nextFilters) > 0) {
      setActiveFilters(nextFilters);
    }
  };

  const handleStudyChange = (nextStudyId: string) => {
    if (!nextStudyId) {
      return;
    }

    setQueryParams(
      {
        filters: {
          ...(queryParams.filters || {}),
          studyId: nextStudyId,
        },
        page: 1,
      },
      'replaceIn'
    );
  };

  const handleManifestDownload = async (row: UploadDashboardRow) => {
    if (!queryParams.filters?.studyId || !row.isManifest) {
      return;
    }

    try {
      setDownloadingManifestKey(row.objectKey);
      const presignedUrl = await getManifestDownloadUrl({
        studyId: queryParams.filters.studyId,
        objectKey: row.objectKey,
        labId,
      });
      window.open(presignedUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Failed to download manifest file', error);
    } finally {
      setDownloadingManifestKey(null);
    }
  };

  const uploadVisibilityMutation = useMutation(
    ({ row, operation }: { row: UploadDashboardRow; operation: StudyUploadVisibilityOperation }) =>
      setStudyUploadVisibility({
        studyId: selectedStudyId,
        objectKey: row.objectKey,
        labId,
        operation,
      }),
    {
      onMutate: async ({ row, operation }) => {
        pendingUploadVisibilityOperationsRef.current[row.objectKey] = operation;
        setPendingUploadVisibilityOperations((current) => ({ ...current, [row.objectKey]: operation }));
        await queryClient.cancelQueries(studyUploadsQueryKey);
        const previousUploads = queryClient.getQueryData<UploadListResponse>(studyUploadsQueryKey);

        queryClient.setQueryData<UploadListResponse | undefined>(studyUploadsQueryKey, (currentUploads) =>
          updateUploadVisibility(currentUploads, row, operation)
        );

        return { previousUploads };
      },
      onSuccess: (_response, { row, operation }) => {
        enqueueSnackbar(`${row.fileName || row.name} ${operation === 'hide' ? 'hidden' : 'restored'}.`, {
          variant: 'success',
        });
      },
      onError: (error: ResponseError, variables, context) => {
        queryClient.setQueryData<UploadListResponse | undefined>(studyUploadsQueryKey, (currentUploads) =>
          restoreUploadVisibility(currentUploads, context?.previousUploads, variables.row.objectKey)
        );
        const actionDescription = `${variables.operation} ${variables.row.fileName || variables.row.name}`;
        enqueueErrorSnackbar(error, actionDescription, () => uploadVisibilityMutation.mutate(variables));
      },
      onSettled: async (_response, _error, { row }) => {
        delete pendingUploadVisibilityOperationsRef.current[row.objectKey];
        setPendingUploadVisibilityOperations((current) => {
          const next = { ...current };
          delete next[row.objectKey];
          return next;
        });
        if (Object.keys(pendingUploadVisibilityOperationsRef.current).length === 0) {
          await invalidateSlideVisibility(queryClient, selectedStudyId);
        }
      },
    }
  );

  const handleSetUploadVisibility = async (row: UploadDashboardRow, operation: StudyUploadVisibilityOperation) => {
    if (pendingUploadVisibilityOperationsRef.current[row.objectKey]) {
      return;
    }

    const displayName = row.fileName || row.name;
    const confirmed = await confirm({
      title: `${operation === 'hide' ? 'Hide' : 'Restore'} uploaded file?`,
      text:
        operation === 'hide'
          ? `Hide ${displayName} from ordinary uploads and slide organization? The file and ingest history will remain unchanged and it can be restored from Hidden.`
          : `Restore ${displayName} to ordinary uploads and slide organization? Archived ingests remain rejected.`,
      confirmButtonProps: {
        title: operation === 'hide' ? 'Hide' : 'Restore',
        color: operation === 'hide' ? 'warning' : 'primary',
      },
    });

    if (confirmed && !pendingUploadVisibilityOperationsRef.current[row.objectKey]) {
      uploadVisibilityMutation.mutate({ row, operation });
    }
  };

  const handleOpenUploadIngestDialog = React.useCallback((row: UploadDashboardRow) => {
    setSelectedUploadForExecution(row);
  }, []);

  const handleCloseUploadIngestDialog = React.useCallback(() => {
    setSelectedUploadForExecution(null);
  }, []);

  const handleOpenBulkUploadIngestDialog = React.useCallback(() => {
    setIsBulkUploadIngestDialogOpen(true);
  }, []);

  const handleCloseBulkUploadIngestDialog = React.useCallback(() => {
    setIsBulkUploadIngestDialogOpen(false);
  }, []);

  const handleOpenManualPostIngestDialog = React.useCallback((row: UploadDashboardRow) => {
    setSelectedUploadForManualPostIngest(row);
  }, []);

  const handleCloseManualPostIngestDialog = React.useCallback(() => {
    setSelectedUploadForManualPostIngest(null);
  }, []);

  const openManifestDialogForUploads = React.useCallback(
    ({ uploadRows, groupIntoSingleCase }: { uploadRows: UploadDashboardRow[]; groupIntoSingleCase: boolean }) => {
      if (!selectedStudyId) {
        return;
      }

      const manifestDialogUrl = getManifestDialogUrlForUploads({
        uploadRows,
        groupIntoSingleCase,
        labId,
        studyId: selectedStudyId,
        returnTo: {
          origin: 'uploads',
          labId,
          studyId: selectedStudyId,
          page: queryParams.page,
          searchTerm: queryParams.searchTerm || undefined,
          uploadFilters: visibleActiveFilters,
          anchor: uploadRows[0]?.objectKey,
        },
      });

      if (!manifestDialogUrl) {
        return;
      }

      navigate(manifestDialogUrl);
    },
    [labId, navigate, queryParams.page, queryParams.searchTerm, selectedStudyId, visibleActiveFilters]
  );

  const handleAssignUploadToOwnCase = React.useCallback(
    (row: UploadDashboardRow) => {
      openManifestDialogForUploads({ uploadRows: [row], groupIntoSingleCase: false });
    },
    [openManifestDialogForUploads]
  );

  const handleAssignSelectedUploadsToSingleCase = React.useCallback(
    (selectedRows: UploadDashboardRow[]) => {
      openManifestDialogForUploads({ uploadRows: selectedRows, groupIntoSingleCase: true });
    },
    [openManifestDialogForUploads]
  );

  const handleAssignSelectedUploadsToOwnCases = React.useCallback(
    (selectedRows: UploadDashboardRow[]) => {
      openManifestDialogForUploads({ uploadRows: selectedRows, groupIntoSingleCase: false });
    },
    [openManifestDialogForUploads]
  );

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      sx={{
        pt: 2,
        flex: 1,
        minHeight: 0,
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Grid item>
        <PageHeader
          pageTitle={currentStudy?.name ? `Study ${currentStudy.name} - Uploaded Files` : 'Uploaded Files'}
          onBackClick={() => navigate(`/procedures?${encodedFilters}`)}
        />
      </Grid>
      <Grid item sx={{ mt: 2, flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Grid
          container
          direction="column"
          spacing={1}
          wrap="nowrap"
          sx={{
            height: (theme) => `calc(100% + ${theme.spacing(1)})`,
            minHeight: 0,
            [`& > .${gridClasses.item}:last-child`]: {
              py: 0,
            },
          }}
        >
          {!currentStudy && (
            <Grid item>
              <LabelledDropdown
                label="Study"
                value={selectedStudyId}
                options={studyOptions}
                onOptionSelected={handleStudyChange}
                loading={studiesLoading}
                sx={{ width: '100%', maxWidth: 360 }}
              />
            </Grid>
          )}
          {currentLab?.isTestLab && canExecuteUploadIngest && currentStudy && selectedStudyId && (
            <Grid item>
              <TestLabSourceIngest
                key={`${labId}:${selectedStudyId}`}
                labId={labId}
                studyId={selectedStudyId}
                studyName={currentStudy.name}
              />
            </Grid>
          )}
          {canLoadUploads ? (
            isLoading ? (
              <Grid item sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
                <Loader stage={LoadingStage.LoadingUploads} />
              </Grid>
            ) : (
              <>
                {isError ? (
                  <Grid item>
                    <Alert
                      severity={data ? 'warning' : 'error'}
                      action={
                        <Button color="inherit" size="small" onClick={() => refetch()}>
                          Retry
                        </Button>
                      }
                    >
                      {data
                        ? 'Status changed. Refresh before assigning or ingesting an upload.'
                        : 'Uploaded files are unavailable.'}
                    </Alert>
                  </Grid>
                ) : null}
                {isRefreshingUploads ? (
                  <Grid item>
                    <Grid container alignItems="center" spacing={1} wrap="nowrap" aria-live="polite">
                      <Grid item>
                        <CircularProgress size={16} />
                      </Grid>
                      <Grid item>
                        <Typography variant="caption">Refreshing uploaded files...</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : null}
                <Grid item container justifyContent="space-between" alignItems="center" gap={1}>
                  <Grid item>
                    <CountedToggleButtonGroup
                      value={visibleActiveFilters}
                      onChange={(nextFilters) => handleFilterChange(nextFilters as UploadFilter[])}
                      options={map(uploadFilters, (uploadFilter) => ({
                        value: uploadFilter,
                        label: uploadFilterLabels[uploadFilter],
                        count: uploadFilterCounts[uploadFilter],
                      }))}
                    />
                  </Grid>
                  {canViewStudyPendingSlides ? (
                    <Grid item>
                      <Button
                        variant="outlined"
                        component={RouterLink}
                        to={getPendingSlidesLocation({ labId, studyId: selectedStudyId })}
                      >
                        View study lost and found slides
                      </Button>
                    </Grid>
                  ) : null}
                </Grid>
                <Grid item sx={{ minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {hasAnyUploads ? (
                    size(rows) > 0 ? (
                      <UploadsTable
                        rows={rows}
                        selectedStudyId={selectedStudyId}
                        onDownloadManifest={handleManifestDownload}
                        onExecuteUploadIngest={handleOpenUploadIngestDialog}
                        onRunManualPostIngestPipeline={handleOpenManualPostIngestDialog}
                        onExecuteBulkUploadIngest={
                          canExecuteBulkUploadIngest ? handleOpenBulkUploadIngestDialog : undefined
                        }
                        onAssignUploadToOwnCase={canUseSlidesManifest ? handleAssignUploadToOwnCase : undefined}
                        onAssignSelectedUploadsToSingleCase={
                          canUseSlidesManifest ? handleAssignSelectedUploadsToSingleCase : undefined
                        }
                        onAssignSelectedUploadsToOwnCases={
                          canUseSlidesManifest ? handleAssignSelectedUploadsToOwnCases : undefined
                        }
                        canExecuteUploadIngest={canExecuteUploadIngest}
                        canReingestUploadedSlides={canReingestUploadedSlides}
                        canRunManualPostIngestPipeline={canRunManualPostIngestPipeline}
                        canManageUploadVisibility={canManageUploadVisibility}
                        onSetUploadVisibility={handleSetUploadVisibility}
                        uploadVisibilityPendingOperations={pendingUploadVisibilityOperations}
                        downloadingManifestKey={downloadingManifestKey}
                        lifecycleRequestState={isError ? 'stale' : 'ready'}
                      />
                    ) : (
                      <Typography variant="caption">No uploaded files match the current filters.</Typography>
                    )
                  ) : (
                    <Typography variant="caption">No uploaded files were found for this study.</Typography>
                  )}
                </Grid>
              </>
            )
          ) : (
            <Grid item>
              <Typography variant="caption">Please select a study to view the uploaded file list.</Typography>
            </Grid>
          )}
        </Grid>
      </Grid>
      {selectedUploadForExecution && selectedStudyId ? (
        <UploadIngestExecutionDialog
          row={selectedUploadForExecution}
          studyId={selectedStudyId}
          labId={labId}
          onClose={handleCloseUploadIngestDialog}
        />
      ) : null}
      {isBulkUploadIngestDialogOpen && selectedStudyId ? (
        <BulkUploadIngestExecutionDialog
          rows={slideUploadRows}
          studyId={selectedStudyId}
          labId={labId}
          canReingestUploadedSlides={canReingestUploadedSlides}
          onClose={handleCloseBulkUploadIngestDialog}
        />
      ) : null}
      {selectedUploadForManualPostIngest && selectedStudyId && selectedUploadForManualPostIngest.slideId ? (
        <JobModal
          jobType={JobType.ManualPostIngestPipeline}
          onClose={handleCloseManualPostIngestDialog}
          casesParams={{
            studyId: selectedStudyId,
            labId,
            filters: { studyId: selectedStudyId },
            slideIdsToInclude: [selectedUploadForManualPostIngest.slideId],
            slidesMode: true,
          }}
        />
      ) : null}
    </Grid>
  );
};

export default wrapPage(
  UploadsDashboard,
  {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
  },
  false,
  {
    bodySx: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
    },
  }
);
